import { Card, CardContent } from '@/components/ui/card';
import { Flame, Award, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StreakCounterWidgetProps {
  config: {
    title?: string;
    streakType?: 'days' | 'weeks';
    showBestStreak?: boolean;
    milestones?: number[];
  };
  data?: {
    currentStreak?: number;
    bestStreak?: number;
    lastActiveDate?: string;
  };
}

export function StreakCounterWidget({ config, data }: StreakCounterWidgetProps) {
  const currentStreak = data?.currentStreak ?? 0;
  const bestStreak = data?.bestStreak ?? currentStreak;
  const title = config.title || 'Current Streak';
  const unit = config.streakType === 'weeks' ? 'weeks' : 'days';

  // Check if current streak is a milestone
  const milestones = config.milestones || [7, 14, 30, 60, 90];
  const isMilestone = milestones.includes(currentStreak);
  const nextMilestone = milestones.find(m => m > currentStreak) || milestones[milestones.length - 1];

  return (
    <Card className={cn(
      "h-full transition-all",
      isMilestone && "ring-2 ring-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20"
    )}>
      <CardContent className="p-4 flex flex-col items-center justify-center h-full text-center">
        <div className={cn(
          "p-3 rounded-full mb-3",
          currentStreak > 0 ? "bg-orange-100 dark:bg-orange-900/30" : "bg-muted"
        )}>
          <Flame className={cn(
            "h-8 w-8",
            currentStreak > 0 ? "text-orange-500" : "text-muted-foreground"
          )} />
        </div>
        
        <p className="text-sm text-muted-foreground font-medium">{title}</p>
        
        <div className="mt-2 flex items-baseline gap-1">
          <span className="text-4xl font-bold text-foreground">{currentStreak}</span>
          <span className="text-lg text-muted-foreground">{unit}</span>
        </div>

        {isMilestone && (
          <div className="flex items-center gap-1 mt-2 text-yellow-600 dark:text-yellow-400">
            <Award className="h-4 w-4" />
            <span className="text-sm font-medium">Milestone reached!</span>
          </div>
        )}

        {config.showBestStreak && bestStreak > currentStreak && (
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Best: {bestStreak} {unit}
          </p>
        )}

        {!isMilestone && currentStreak > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            {nextMilestone - currentStreak} more to next milestone
          </p>
        )}
      </CardContent>
    </Card>
  );
}
